import Box from '@mui/material/Box';
import { alpha, type SxProps, type Theme } from '@mui/material/styles';
import type { ReactNode } from 'react';

export type LabelColor =
  | 'default'
  | 'primary'
  | 'secondary'
  | 'info'
  | 'success'
  | 'warning'
  | 'error';

export type LabelVariant = 'filled' | 'outlined' | 'soft';

export type LabelProps = {
  children: ReactNode;
  color?: LabelColor;
  variant?: LabelVariant;
  startIcon?: ReactNode;
  sx?: SxProps<Theme>;
};

export function Label({
  children,
  color = 'default',
  variant = 'soft',
  startIcon,
  sx,
}: LabelProps) {
  return (
    <Box
      component="span"
      sx={[
        (theme) => {
          const paletteColor = color === 'default' ? theme.palette.grey : theme.palette[color];
          const main =
            color === 'default' ? theme.palette.text.secondary : theme.palette[color].main;

          const variantStyles = {
            soft: {
              color: color === 'default' ? theme.palette.text.secondary : theme.palette[color].dark,
              backgroundColor: alpha(main, 0.16),
            },
            filled: {
              color:
                color === 'default'
                  ? theme.palette.common.white
                  : theme.palette[color].contrastText,
              backgroundColor: main,
            },
            outlined: {
              color: main,
              backgroundColor: 'transparent',
              border: `1px solid ${alpha(main, 0.48)}`,
            },
          } as const;

          return {
            height: 24,
            minWidth: 24,
            lineHeight: 0,
            borderRadius: 1,
            cursor: 'default',
            alignItems: 'center',
            whiteSpace: 'nowrap',
            display: 'inline-flex',
            justifyContent: 'center',
            paddingInline: '6px',
            fontSize: theme.typography.pxToRem(12),
            fontWeight: 700,
            gap: 0.75,
            ...(paletteColor ? {} : {}),
            ...variantStyles[variant],
          };
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      {startIcon}
      {children}
    </Box>
  );
}
